import './res/styles/styles.scss';
import {BeLogged, LogClient_Browser, merge, Module} from "@intuitionrobotics/ts-common";

import {App} from "./app/App";
import {RoutingModule, StorageModule, Thunder, ToastModule, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {ContactsModule} from "@modules/ContactsModule";
import {PackageManagerModule} from "@modules/package-manager/PackageManagerModule";
import {LoginModule} from "@modules/LoginModule";
import {AppModule} from '@modules/AppModule';
import {EnvironmentModule} from "@modules/EnvironmentModule";
import {HeaderKey_CurrentPage} from "@app/ir-q-app-common/types/auth";
import {ConfirmationModule} from "@modules/confirmation-modal-module";
import {OverridesModule} from "@modules/OverridesModule";
import {AccountModule} from "@intuitionrobotics/user-account/frontend";
import {PermissionsFE} from "@intuitionrobotics/permissions/frontend";
import {PROJECT_IDENTITY} from "@app-sp/app-shared/shared";
import {BugReportModule} from "@intuitionrobotics/bug-report/app-frontend/modules/BugReportModule";
import {BaseHttpRequest} from '@intuitionrobotics/thunderstorm';
import {CareCenterModule} from "@modules/CareCenterModule";
import {DataEnvsModule} from "@modules/DataEnvsModule";
import {FirebaseModule} from "@intuitionrobotics/firebase/frontend";
import {ActivationsModule} from "@modules/ActivationsModule";
import {UnitsManagerModule} from "@modules/UnitsManagerModule";
import {ContactListModule} from "@modules/ContactListModule";
import {UnitsModule} from "@modules/UnitsModule";
import {PermissionsModule} from "@modules/PermissionsModule";
import {PushPubSubModule} from "@intuitionrobotics/push-pub-sub/app-frontend/modules/PushPubSubModule";
import {UploaderModule} from "@intuitionrobotics/file-upload/frontend";
import {APP_TOAST_BUILDER} from "@components/APP_TOAST_BUILDER";
import {config} from "./config";
import {config as defaultConfig} from "./defaultConfig"
import {reloadOnceForStaleChunk} from "./app/chunkReload";

BeLogged.addClient(LogClient_Browser);

// Global catch-all for errors that never reach a React error boundary —
// uncaught exceptions and unhandled promise rejections (e.g. a failed token /
// session refresh on tab-reactivation). Logged to the server so they surface in
// logzio alongside request failures. Best-effort; must never throw here.
const logGlobal = (kind: string, err: unknown) => {
    try {
        const e = err as (Error | undefined);
        const msg = e?.stack || e?.message || String(err);
        XhrHttpModule.logError(`[GlobalError:${kind}] ${window.location.href} :: ${msg}`);
    } catch { /* ignore */ }
};
if (typeof window !== "undefined") {
    window.addEventListener("error", (e: ErrorEvent) => logGlobal("error", e.error || e.message));
    window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => logGlobal("unhandledrejection", e.reason));

    // A code-split route chunk 404s after a redeploy (old tab, new build). Vite
    // fires this when a dynamic import fails; the build is stale, so reload once
    // to pull the fresh chunk hashes instead of blanking / erroring.
    window.addEventListener("vite:preloadError", (e: Event) => {
        e.preventDefault();
        reloadOnceForStaleChunk();
    });
}

XhrHttpModule.addDefaultHeader(HeaderKey_CurrentPage, () => window.location.href);
XhrHttpModule.addDefaultResponseHandler((request: BaseHttpRequest<any>) => {
    if (request.getStatus() >= 400)
        XhrHttpModule.logError(`Request: ${request.key} failed with status ${request.getStatus()} and error: ${request.getErrorMessage() || request.getErrorResponse()}`)

    return false;
});

const modules: Module[] = [
    PushPubSubModule,
    UnitsModule,
    FirebaseModule,
    PermissionsModule,
    EnvironmentModule,
    ConfirmationModule, // TODO: replace with latest Dialog Module
    LoginModule,
    StorageModule,
    ContactsModule,
    RoutingModule,
    AccountModule,
    AppModule,
    PackageManagerModule,
    OverridesModule,
    BugReportModule,
    CareCenterModule,
    DataEnvsModule,
    ActivationsModule,
    UnitsManagerModule,
    ContactListModule,
    UploaderModule
];

// Offline: drop PushPubSubModule entirely. Push (FCM) needs real Firebase + a
// backend register endpoint, neither available offline; its only callers
// (Environment/Permissions onLoginStatusUpdated) are already gated. Removing it
// from the module list stops the eager service-worker registration + FCM token
// fetch (the last client-side external call).
const activeModules = config.EnvironmentModule?.offline
    ? modules.filter(m => m !== PushPubSubModule)
    : modules;

PermissionsFE.setDefaultConfig({projectId: PROJECT_IDENTITY._id});

ToastModule.setDefaultBuilder(APP_TOAST_BUILDER);

// Offline-first local dev: seed a fake session AND the data modules BEFORE the
// modules init — AccountModule.init() must find a JWT (else it redirects to the
// unreachable Kaspero login), and login-triggered hooks (e.g. ActivationsModule.list)
// read seeded fixtures instead of the network. The offline bootstrap + fixtures are
// DYNAMICALLY imported so they're code-split into a chunk that never loads in online
// builds — zero bundle/runtime impact when not offline. Online, the `if` is false so
// no await is hit and build() runs synchronously in the same tick (unchanged behavior).
void (async () => {
    if (config.EnvironmentModule?.offline) {
        const [{bootstrapOfflineAuth}, {bootstrapOfflineUnits}] = await Promise.all([
            import("./offline/bootstrap"),
            import("./app/pages/units/offline/bootstrap")
        ]);
        bootstrapOfflineAuth();
        bootstrapOfflineUnits();
    }

    new Thunder()
        .setConfig(merge(defaultConfig, config))
        .addModules(...activeModules)
        .setMainApp(App)
        .build();
})();
